import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR, formatSqft, formatApiError } from "@/lib/format";
import { LeafletMap } from "@/components/LeafletMap";
import { VisitScheduler } from "@/components/VisitScheduler";
import { PriceInsight } from "@/components/PriceInsight";
import { ComparableListings } from "@/components/ComparableListings";
import { DocumentVault } from "@/components/DocumentVault";
import { DocumentStatusTracker } from "@/components/DocumentStatusTracker";
import { VerifiedSeal } from "@/components/VerifiedSeal";
import { MapPin, BedDouble, Ruler, Phone, Mail, ArrowLeft, Check, Clock, ShieldCheck, AlertTriangle, Video, FileCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const PropertyDetail = ({ onAuth }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [inqOpen, setInqOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "I'm interested in this property. Please contact me." });
  const [sending, setSending] = useState(false);
  const [vaultOpenSignal, setVaultOpenSignal] = useState(0);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setP(data));
  }, [id]);

  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-[var(--ink-muted)]">Loading property...</div>;

  const sendInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, property_id: p.id });
      toast.success("Inquiry sent! Opening WhatsApp to notify the agent…");
      setInqOpen(false);
      // Auto-forward to agent via WhatsApp deep link
      if (data.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank", "noopener");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid={`property-detail-${p.id}`}>
      <Link to="/listings" className="inline-flex items-center gap-2 text-sm text-[var(--ink-muted)] hover:text-[var(--teak)] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to listings
      </Link>

      {/* Status banner for owner/admin viewing non-approved listings */}
      {(user?.id === p.owner_id || user?.role === "admin") && p.status !== "approved" && (
        <div className={`mb-6 p-4 rounded-2xl border flex items-start gap-3 ${
          p.status === "pending"
            ? "bg-[rgba(212,163,89,0.1)] border-[rgba(212,163,89,0.35)]"
            : "bg-[rgba(200,90,50,0.1)] border-[rgba(200,90,50,0.3)]"
        }`} data-testid={`status-banner-${p.status}`}>
          {p.status === "pending" ? <Clock className="w-5 h-5 text-[var(--gold)] mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-[var(--primary)] mt-0.5" />}
          <div className="text-sm">
            <div className="font-semibold text-[var(--teak)] capitalize">Status: {p.status}</div>
            <div className="text-[var(--ink-muted)] mt-1">
              {p.status === "pending" && "Awaiting admin verification. Not yet visible to buyers."}
              {p.status === "rejected" && `Rejected${p.verification_note ? `: ${p.verification_note}` : ""}. Please re-post with corrections.`}
            </div>
          </div>
        </div>
      )}
      {p.status === "approved" && (user?.id === p.owner_id) && (
        <div className="mb-6 p-3 rounded-2xl bg-[rgba(30,67,56,0.08)] border border-[rgba(30,67,56,0.2)] flex items-center gap-2 text-sm text-[var(--emerald)]" data-testid="status-banner-approved">
          <ShieldCheck className="w-4 h-4" /> Verified &amp; live on NimmaSwathu.
        </div>
      )}

      {/* Gallery */}
      <div className="grid lg:grid-cols-4 gap-3 mb-10">
        <div className="lg:col-span-3 aspect-[16/10] rounded-2xl overflow-hidden relative">
          <img src={p.images?.[activeImg]} alt={p.title} className="w-full h-full object-cover" />
          {p.verification_tier === "document_verified" && p.status === "approved" && (
            <VerifiedSeal size="md" />
          )}
        </div>
        <div className="lg:col-span-1 grid grid-cols-3 lg:grid-cols-1 gap-3">
          {(p.images || []).slice(0, 3).map((img, i) => (
            <button key={i} onClick={() => setActiveImg(i)} className={`aspect-[4/3] rounded-xl overflow-hidden border-2 ${activeImg === i ? "border-[var(--primary)]" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`chip ${p.listing_type === "rent" ? "chip-emerald" : "chip-gold"}`}>
                {p.listing_type === "rent" ? "For Rent" : "For Sale"}
              </span>
              <span className="chip">{p.property_type.replace("_", " ")}</span>
              {p.verification_tier === "document_verified" && p.status === "approved" && (
                <span className="chip-gold chip" data-testid="document-verified-badge">
                  <FileCheck className="w-3 h-3" /> Document Verified
                </span>
              )}
              {p.virtual_tour_url && p.status === "approved" && (
                <span className="chip-emerald chip" data-testid="verified-virtual-badge">
                  <Video className="w-3 h-3" /> Verified Virtual
                </span>
              )}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl text-[var(--teak)] leading-tight">{p.title}</h1>
            <div className="flex items-center gap-1 text-[var(--ink-muted)] mt-3">
              <MapPin className="w-4 h-4" />
              <span>{p.address} · {p.locality}, Mysuru</span>
            </div>
            {p.virtual_tour_url && (
              <a
                href={p.virtual_tour_url}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost mt-4 inline-flex"
                data-testid="virtual-tour-btn"
              >
                <Video className="w-4 h-4" /> Watch Virtual Tour
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl border border-[var(--line)] bg-white">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">{p.listing_type === "rent" ? "Rent" : "Price"}</div>
              <div className="font-mono text-xl font-bold text-[var(--primary)] mt-1">{formatINR(p.price, p.listing_type)}</div>
            </div>
            <div className="p-5 rounded-2xl border border-[var(--line)] bg-white">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">BHK</div>
              <div className="flex items-center gap-1 mt-1 font-serif text-xl text-[var(--teak)]"><BedDouble className="w-4 h-4" /> {p.bhk || "—"}</div>
            </div>
            <div className="p-5 rounded-2xl border border-[var(--line)] bg-white">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">Area</div>
              <div className="flex items-center gap-1 mt-1 font-serif text-xl text-[var(--teak)]"><Ruler className="w-4 h-4" /> {formatSqft(p.area_sqft)}</div>
            </div>
            <div className="p-5 rounded-2xl border border-[var(--line)] bg-white">
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">Furnishing</div>
              <div className="font-serif text-xl text-[var(--teak)] mt-1 capitalize">{(p.furnishing || "—").replace("-", " ")}</div>
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[var(--teak)] mb-3">About this property</h2>
            <p className="text-[var(--ink-muted)] leading-relaxed">{p.description}</p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[var(--teak)] mb-3">Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {(p.amenities || []).map((a) => (
                <div key={a} className="flex items-center gap-2 text-sm text-[var(--ink)]">
                  <Check className="w-4 h-4 text-[var(--emerald)]" /> {a}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-[var(--teak)] mb-3">Location</h2>
            <div className="h-[380px]">
              <LeafletMap properties={[p]} center={[p.lat, p.lng]} zoom={15} />
            </div>
          </div>
        </div>

        {/* Sidebar — Agent card */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <DocumentStatusTracker property={p} onUploadRequest={() => setVaultOpenSignal((s) => s + 1)} />
            <PriceInsight propertyId={p.id} />
            <ComparableListings propertyId={p.id} />
            <DocumentVault property={p} onAuth={onAuth} openSignal={vaultOpenSignal} />
            <div className="p-6 rounded-2xl border border-[var(--line)] bg-white space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">Listed by</div>
              <div className="font-serif text-xl text-[var(--teak)] mt-1">{p.agent_name || p.owner_name}</div>
              <div className="text-xs text-[var(--ink-muted)] mt-1 capitalize">{p.posted_by_role || "owner"}</div>
            </div>
            <div className="space-y-2 text-sm">
              {p.agent_phone && !p.sold && (
                <a href={`tel:${p.agent_phone}`} className="flex items-center gap-2 text-[var(--ink)] hover:text-[var(--primary)]">
                  <Phone className="w-4 h-4" /> {p.agent_phone}
                </a>
              )}
              {p.owner_email && !p.sold && (
                <a href={`mailto:${p.owner_email}`} className="flex items-center gap-2 text-[var(--ink)] hover:text-[var(--primary)] break-all">
                  <Mail className="w-4 h-4" /> {p.owner_email}
                </a>
              )}
            </div>
            {p.sold ? (
              <div
                className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-900 text-center"
                data-testid="sold-notice"
              >
                <div className="font-serif text-lg">This property has been sold</div>
                <div className="text-xs text-red-700 mt-1">Browse similar listings in {p.locality}, Mysuru.</div>
              </div>
            ) : (<>
            <Dialog open={inqOpen} onOpenChange={setInqOpen}>
              <DialogTrigger asChild>
                <button className="btn-primary w-full" data-testid="inquiry-open-btn">Send Inquiry</button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl">Send an inquiry</DialogTitle>
                </DialogHeader>
                <form onSubmit={sendInquiry} className="space-y-3">
                  <div>
                    <Label>Name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquiry-name" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message" />
                  </div>
                  <button type="submit" disabled={sending} className="btn-primary w-full" data-testid="inquiry-submit">
                    {sending ? "Sending..." : "Send Inquiry"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>
            {p.agent_phone && (
              <a
                href={`https://wa.me/${p.agent_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi, I'm interested in "${p.title}" at ${p.locality}, Mysuru. Is it still available?`)}`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost w-full"
                data-testid="whatsapp-btn"
              >
                Chat on WhatsApp
              </a>
            )}
            <VisitScheduler property={p} onAuth={onAuth} />
            </>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
